import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './PaymentManager.css';
import {
    Euro, CheckCircle2, AlertCircle, Search, ChevronLeft, ChevronRight,
    X, Undo2, MoreHorizontal, User, Edit2, AlertTriangle, UserMinus,
    FileSpreadsheet, Calendar, List, Zap, Filter, ArrowDownToLine, Users, ChevronDown,
    FileText, ExternalLink, Loader2, Receipt, Check, RotateCcw, Mail, Printer
} from 'lucide-react';
import PaymentHistorySocio from '../PaymentHistorySocio/PaymentHistorySocio';
import DailyInvoicesModal from './DailyInvoicesModal';
import PersonWizardModal from './PersonWizardModal';
import API_BASE_URL from '../../config/api';

const PaymentManager = () => {
    // Data State
    const [athletes, setAthletes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [teams, setTeams] = useState([]);
    const [sports, setSports] = useState([]);

    // Filter & Search State
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterTeam, setFilterTeam] = useState('all');
    const [filterSport, setFilterSport] = useState('all');
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

    // Pagination State
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Detailed Profile Modal
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    // Member edit wizard (shared with PeopleManager)
    const [editMemberUserId, setEditMemberUserId] = useState(null);

    // Month Selection Modal State
    const [isMonthModalOpen, setIsMonthModalOpen] = useState(false);
    const [selectedAthlete, setSelectedAthlete] = useState(null);
    const [modalYear, setModalYear] = useState(new Date().getFullYear());
    const [selectedPeriods, setSelectedPeriods] = useState([]);
    const [selectedInscriptions, setSelectedInscriptions] = useState([]);
    const [monthlyQuotas, setMonthlyQuotas] = useState({});
    const [modalInscriptions, setModalInscriptions] = useState([]);

    // Dedicated Invoicing Modal State
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [invoiceModalAthlete, setInvoiceModalAthlete] = useState(null);
    const [invoiceModalYear, setInvoiceModalYear] = useState(new Date().getFullYear());
    const [invoiceModalPeriods, setInvoiceModalPeriods] = useState([]);
    const [invoiceModalInscriptions, setInvoiceModalInscriptions] = useState([]);
    const [invoiceModalMonthlyQuotas, setInvoiceModalMonthlyQuotas] = useState({});
    const [invoiceModalInscriptionsList, setInvoiceModalInscriptionsList] = useState([]);
    const [invoiceNif, setInvoiceNif] = useState('');
    const [invoiceName, setInvoiceName] = useState('');
    const [invoiceEmail, setInvoiceEmail] = useState('');
    const [paymentMethodName, setPaymentMethodName] = useState('Numerário');
    const [invoiceDeliveryMethod, setInvoiceDeliveryMethod] = useState('both'); // 'both' | 'email' | 'paper'
    const [isSubmittingInvoice, setIsSubmittingInvoice] = useState(false);
    const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
    const [showInvoiceSuccessModal, setShowInvoiceSuccessModal] = useState(false);
    const [invoiceResult, setInvoiceResult] = useState(null);
    const [isDailyInvoicesModalOpen, setIsDailyInvoicesModalOpen] = useState(false);





    // Membership number inline editing
    const [editingMembershipId, setEditingMembershipId] = useState(null);
    const [editingMembershipValue, setEditingMembershipValue] = useState('');
    const [membershipDuplicates, setMembershipDuplicates] = useState({});

    // Quota inline editing
    const [editingQuotaUserId, setEditingQuotaUserId] = useState(null);
    const [editingQuotaValue, setEditingQuotaValue] = useState('');

    // Breakdown popover
    const [breakdownPopover, setBreakdownPopover] = useState({ userId: null, data: null, loading: false, x: 0, y: 0, placement: 'bottom' });

    const fetchPaymentStatuses = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;

            let url = `${apiUrl}/payment/admin/athletes-status?month=${currentMonth}&year=${currentYear}&page=${page}&pageSize=${pageSize}`;
            if (debouncedSearchTerm) url += `&search=${encodeURIComponent(debouncedSearchTerm)}`;
            if (filterStatus !== 'all') url += `&status=${filterStatus}`;

            // Map 'coach' filter value to special backend ID -3, otherwise use the selected ID
            if (filterTeam === 'coach') {
                url += `&teamId=-3`;
            } else if (filterTeam !== 'all') {
                url += `&teamId=${filterTeam}`;
            }

            if (filterSport !== 'all') url += `&sportId=${filterSport}`;

            const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error('Failed to fetch payment statuses');

            const data = await response.json();
            setAthletes(data.items || []);
            setTotalCount(data.totalCount || 0);
            setTotalPages(data.totalPages || 1);
        } catch (error) {
            console.error('Error fetching payments:', error);
            setAthletes([]);
        } finally {
            setLoading(false);
        }
    }, [currentMonth, currentYear, page, pageSize, debouncedSearchTerm, filterStatus, filterTeam, filterSport]);

    useEffect(() => { fetchPaymentStatuses(); }, [fetchPaymentStatuses]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        const fetchFilters = async () => {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            try {
                const [teamsRes, sportsRes] = await Promise.all([
                    fetch(`${apiUrl}/teams`, { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch(`${apiUrl}/sports`, { headers: { 'Authorization': `Bearer ${token}` } })
                ]);
                if (teamsRes.ok) { const d = await teamsRes.json(); setTeams(Array.isArray(d) ? d : []); }
                if (sportsRes.ok) { const d = await sportsRes.json(); setSports(Array.isArray(d) ? d : []); }
            } catch (error) {
                console.error('Error fetching filters:', error);
            }
        };
        fetchFilters();
    }, []);

    // Pre-fill modal with payment history when opened or when modal year changes
    useEffect(() => {
        if (!isMonthModalOpen || !selectedAthlete) return;

        const fetchHistoryForModal = async () => {
            try {
                const token = localStorage.getItem('token');
                const apiUrl = `${API_BASE_URL}/api`;

                const [resHistory, resQuotas] = await Promise.all([
                    fetch(`${apiUrl}/payment/history?userId=${selectedAthlete.userId}&year=${modalYear}&month=1`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }),
                    fetch(`${apiUrl}/payment/admin/user-year-quotas?userId=${selectedAthlete.userId}&year=${modalYear}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                ]);

                const data = await resHistory.json();
                const quotasData = await resQuotas.json();

                if (quotasData && quotasData.quotas) {
                    setMonthlyQuotas(quotasData.quotas);
                } else {
                    setMonthlyQuotas({});
                }

                const histInsc = (data.inscriptions || []).filter(i => !i.paid && i.amount > 0);
                const effectiveInsc = histInsc.length > 0
                    ? histInsc
                    : (selectedAthlete.pendingInscriptions || []);
                setModalInscriptions(effectiveInsc);

                const historyPayments = data.payments || [];
                const isQuotaPayment = (p) => {
                    const desc = (p.description || '').toLowerCase();
                    return !desc.includes('inscrição') && !desc.includes('inscricao');
                };
                const quotaHistoryPayments = historyPayments.filter(isQuotaPayment);

                const isAnnual = selectedAthlete.paymentPreference === 'Annual';

                if (isAnnual) {
                    const annualPay = quotaHistoryPayments.find(p =>
                        p.amount > 0 &&
                        p.paymentMethod !== 'Histórico' &&
                        ((!p.periodMonth || p.periodMonth === 0) || p.description?.includes("Anual") || p.label?.includes("Anual"))
                    );

                    let startYear = currentYear;
                    let startMonth = currentMonth;

                    if (annualPay) {
                        if (annualPay.periodMonth && annualPay.periodYear) {
                            startYear = annualPay.periodYear;
                            startMonth = annualPay.periodMonth;
                        } else {
                            const payDate = annualPay.paymentDate || annualPay.createdAt;
                            if (payDate) {
                                const d = new Date(payDate);
                                startYear = d.getFullYear();
                                startMonth = d.getMonth() + 1;
                            }
                        }
                    }

                    const startAbs = startYear * 12 + startMonth - 1;
                    const annualAmount = annualPay ? annualPay.amount : (selectedAthlete.amount || 0) * 12;
                    const monthlyBase = annualAmount / 12;

                    const coveredPeriods = [];
                    for (let m = 1; m <= 12; m++) {
                        const currentAbs = modalYear * 12 + m - 1;
                        const isAnnualCovered = currentAbs >= startAbs && currentAbs < startAbs + 12;
                        const hasMonthlyPaid = quotaHistoryPayments.some(p => p.periodYear === modalYear && p.periodMonth === m && p.amount > 0);

                        if (isAnnualCovered || hasMonthlyPaid) {
                            const monthQuota = (quotasData.quotas && quotasData.quotas[m - 1]) || selectedAthlete.amount || 0;
                            const monthlyPay = quotaHistoryPayments.find(p => p.periodYear === modalYear && p.periodMonth === m && p.amount > 0);
                            coveredPeriods.push({
                                month: m,
                                year: modalYear,
                                amount: isAnnualCovered ? monthQuota : (monthlyPay ? monthlyPay.amount : monthQuota)
                            });
                        }
                    }
                    setSelectedPeriods(coveredPeriods);
                } else {
                    const preFilled = quotaHistoryPayments
                        .filter(p => p.periodYear === modalYear && p.periodMonth > 0)
                        .map(p => ({
                            month: p.periodMonth,
                            year: p.periodYear,
                            amount: p.amount
                        }));

                    if (preFilled.length === 0 && modalYear === currentYear) {
                        const currentMonthQuota = (quotasData.quotas && quotasData.quotas[currentMonth - 1]) || selectedAthlete.amount || 0;
                        preFilled.push({
                            month: currentMonth,
                            year: currentYear,
                            amount: currentMonthQuota
                        });
                    }
                    setSelectedPeriods(preFilled);
                }

                // Inscrições só devem vir selecionadas se já estiverem pagas; inscrições não pagas vêm desmarcadas (unchecked)
                const paidIds = (data.inscriptions || selectedAthlete.pendingInscriptions || [])
                    .filter(i => i.paid === true)
                    .map(i => i.athleteTeamId);
                setSelectedInscriptions(paidIds);
            } catch (error) {
                console.error('Error fetching history for modal:', error);
            }
        };

        fetchHistoryForModal();
    }, [isMonthModalOpen, selectedAthlete?.userId, modalYear, currentMonth, currentYear]);

    useEffect(() => {
        if (!isInvoiceModalOpen || !invoiceModalAthlete) return;

        const fetchQuotasForInvoiceModal = async () => {
            try {
                const token = localStorage.getItem('token');
                const apiUrl = `${API_BASE_URL}/api`;
                const [resQuotas, resHistory] = await Promise.all([
                    fetch(`${apiUrl}/payment/admin/user-year-quotas?userId=${invoiceModalAthlete.userId}&year=${invoiceModalYear}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }),
                    fetch(`${apiUrl}/payment/history?userId=${invoiceModalAthlete.userId}&year=${invoiceModalYear}&month=1`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                ]);
                if (resQuotas.ok) {
                    const quotasData = await resQuotas.json();
                    if (quotasData && quotasData.quotas) {
                        setInvoiceModalMonthlyQuotas(quotasData.quotas);
                        setInvoiceModalPeriods(prev => prev.map(p => {
                            if (p.year === invoiceModalYear) {
                                return { ...p, amount: quotasData.quotas[p.month - 1] || p.amount };
                            }
                            return p;
                        }));
                    }
                }
                if (resHistory.ok) {
                    const histData = await resHistory.json();
                    const histInsc = (histData.inscriptions || []).filter(i => !i.paid && i.amount > 0);
                    setInvoiceModalInscriptionsList(histInsc.length > 0 ? histInsc : (invoiceModalAthlete.pendingInscriptions || []));
                }
            } catch (error) {
                console.error('Error fetching quotas for invoice modal:', error);
            }
        };

        fetchQuotasForInvoiceModal();
    }, [isInvoiceModalOpen, invoiceModalAthlete, invoiceModalYear]);


    // Reset to page 1 whenever any filter changes (including debounced search)
    useEffect(() => {
        setPage(1);
    }, [debouncedSearchTerm, filterStatus, filterTeam, filterSport, currentMonth, currentYear]);

    const startEditMembership = (athlete) => {
        setEditingMembershipId(athlete.userId);
        setEditingMembershipValue(athlete.membershipNumber || '');
    };

    const cancelEditMembership = () => {
        setEditingMembershipId(null);
        setEditingMembershipValue('');
    };

    const confirmEditMembership = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const response = await fetch(`${apiUrl}/payment/admin/membership-number`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, membershipNumber: editingMembershipValue })
            });
            if (!response.ok) throw new Error('Failed to update membership number');
            const data = await response.json();

            setAthletes(prev => prev.map(a =>
                a.userId === userId ? { ...a, membershipNumber: editingMembershipValue } : a
            ));

            setMembershipDuplicates(prev => ({
                ...prev,
                [userId]: data.duplicate || null
            }));
        } catch (error) {
            console.error('Error updating membership number:', error);
            alert('Erro ao atualizar número de sócio.');
        } finally {
            setEditingMembershipId(null);
            setEditingMembershipValue(0);
        }
    };

    const startEditQuota = (athlete) => {
        setEditingQuotaUserId(athlete.userId);
        const hasCustom = athlete.customQuotaPrice != null && athlete.customQuotaPrice !== '' && !isNaN(Number(athlete.customQuotaPrice));
        setEditingQuotaValue(hasCustom
            ? athlete.customQuotaPrice.toString()
            : athlete.amount?.toString() || '');
    };

    const fetchBreakdown = async (userId, e) => {
        if (breakdownPopover.userId === userId) {
            setBreakdownPopover({ userId: null, data: null, loading: false, x: 0, y: 0, placement: 'bottom' });
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const popoverWidth = 320;
        const popoverEstimatedHeight = 280;

        let x = rect.left;
        if (x + popoverWidth > window.innerWidth) {
            x = Math.max(16, window.innerWidth - popoverWidth - 16);
        }
        if (x < 16) x = 16;

        // Auto-detect whether to place the popup above or below the element
        const spaceBelow = window.innerHeight - rect.bottom;
        const placeTop = spaceBelow < popoverEstimatedHeight && rect.top > spaceBelow;

        const y = placeTop ? (window.innerHeight - rect.top + 8) : (rect.bottom + 8);
        const placement = placeTop ? 'top' : 'bottom';

        setBreakdownPopover({ userId, data: null, loading: true, x, y, placement });
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(
                `${API_BASE_URL}/api/payment/quota?userId=${userId}&month=${currentMonth}&year=${currentYear}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) throw new Error('fetch failed');
            const data = await res.json();
            setBreakdownPopover(prev => ({ ...prev, data, loading: false }));
        } catch {
            setBreakdownPopover(prev => ({ ...prev, data: null, loading: false }));
        }
    };

    const cancelEditQuota = () => {
        setEditingQuotaUserId(null);
        setEditingQuotaValue('');
    };

    const confirmEditQuota = async (userId, explicitValue = undefined) => {
        const valToUse = explicitValue !== undefined ? explicitValue : editingQuotaValue;
        const parsedPrice = (valToUse === '' || valToUse === null || valToUse === undefined) ? null : parseFloat(valToUse);

        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const response = await fetch(`${apiUrl}/payment/admin/custom-quota-price`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, customQuotaPrice: (parsedPrice === null || isNaN(parsedPrice)) ? null : parsedPrice })
            });
            if (!response.ok) throw new Error('Failed to update custom quota price');

            fetchPaymentStatuses();
        } catch (error) {
            console.error('Error updating custom quota price:', error);
            alert('Erro ao atualizar preço de quota.');
        } finally {
            setEditingQuotaUserId(null);
            setEditingQuotaValue('');
        }
    };

    const handleResetCustomQuota = async (userId, athleteName) => {
        if (!window.confirm(`Deseja remover a quota personalizada de ${athleteName ? `"${athleteName}"` : 'este sócio'} e voltar ao cálculo automático do sistema?`)) {
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const response = await fetch(`${apiUrl}/payment/admin/custom-quota-price`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, customQuotaPrice: null })
            });
            if (!response.ok) throw new Error('Failed to reset custom quota price');

            if (breakdownPopover.userId === userId) {
                setBreakdownPopover({ userId: null, data: null, loading: false, x: 0, y: 0, placement: 'bottom' });
            }

            fetchPaymentStatuses();
        } catch (error) {
            console.error('Error resetting custom quota price:', error);
            alert('Erro ao restaurar cálculo automático da quota.');
        }
    };

    const handleMarkWithdrawn = async (athlete) => {
        if (!window.confirm(`Tem a certeza que deseja marcar "${athlete.name}" como desistente?\n\nEsta ação irá desativar a conta e encerrar todas as equipas ativas.`)) return;
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const response = await fetch(`${apiUrl}/payment/admin/mark-withdrawn`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: athlete.userId })
            });
            if (!response.ok) throw new Error('Failed to mark athlete as withdrawn');
            fetchPaymentStatuses();
        } catch (error) {
            console.error('Error marking athlete as withdrawn:', error);
            alert('Erro ao marcar atleta como desistente.');
        }
    };

    const stripEmailAlias = (email) => {
        if (!email) return '';
        const atIdx = email.lastIndexOf('@');
        if (atIdx <= 0) return email.trim();
        const local = email.substring(0, atIdx).trim();
        const domain = email.substring(atIdx).trim();
        const plusIdx = local.indexOf('+');
        const baseLocal = plusIdx >= 0 ? local.substring(0, plusIdx) : local;
        return (baseLocal + domain).toLowerCase();
    };

    const openMonthModalForAthlete = (athlete) => {
        setSelectedAthlete(athlete);
        setModalYear(currentYear);
        setSelectedInscriptions([]);
        setIsMonthModalOpen(true);
    };

    const handleUpdateStatus = async (userId, newStatus, periods = null) => {
        const athlete = athletes.find(a => a.userId === userId);
        const isAnnual = athlete?.paymentPreference === 'Annual';
        const isMonthly = athlete?.paymentPreference === 'Monthly';

        if (newStatus === 'Completed' && (isMonthly || isAnnual) && !periods) {
            openMonthModalForAthlete(athlete);
            return;
        }

        const actionText = newStatus === 'Completed' ? 'validar' : 'reverter';
        let confirmMsg = `Tem a certeza que deseja ${actionText} este pagamento?`;

        if (!window.confirm(confirmMsg)) return;

        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const body = {
                userId,
                periodYear: currentYear,
                status: newStatus,
                markInscriptionsPaid: selectedInscriptions.length > 0 ? selectedInscriptions : null,
                issueInvoice: false
            };

            if (periods) {
                body.selectedPeriods = periods;
            } else {
                body.periodMonth = currentMonth;
            }

            const response = await fetch(`${apiUrl}/payment/admin/manual-payment`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (!response.ok) throw new Error('Failed to update payment');
            setIsMonthModalOpen(false);
            fetchPaymentStatuses();
        } catch (error) {
            console.error('Error updating payment:', error);
            alert('Erro ao atualizar estado de pagamento');
        }
    };

    const handleConfirmModalPayment = async () => {
        if (!selectedAthlete) return;
        if (selectedPeriods.length === 0 && selectedInscriptions.length === 0) return;

        setIsSubmittingPayment(true);
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;

            const body = {
                userId: selectedAthlete.userId,
                periodYear: modalYear,
                status: "Completed",
                markInscriptionsPaid: selectedInscriptions.length > 0 ? selectedInscriptions : null,
                selectedPeriods: selectedPeriods,
                issueInvoice: false
            };

            const response = await fetch(`${apiUrl}/payment/admin/manual-payment`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || 'Falha ao processar pagamento');
            }

            setIsMonthModalOpen(false);
            fetchPaymentStatuses();
        } catch (error) {
            console.error('Error submitting payment:', error);
            alert(error.message || 'Erro ao atualizar estado de pagamento');
        } finally {
            setIsSubmittingPayment(false);
        }
    };

    const togglePeriod = (m, y) => {
        setSelectedPeriods(prev => {
            const exists = prev.find(p => p.month === m && p.year === y);
            if (exists) return prev.filter(p => !(p.month === m && p.year === y));
            const estimatedAmount = (monthlyQuotas && monthlyQuotas[m - 1]) || selectedAthlete?.amount || 0;
            return [...prev, { month: m, year: y, amount: estimatedAmount }];
        });
    };

    const updatePeriodAmount = (m, y, amt) => {
        const val = parseFloat(amt) || 0;
        setSelectedPeriods(prev => prev.map(p =>
            (p.month === m && p.year === y) ? { ...p, amount: val } : p
        ));
    };

    const toggleInscription = (id) => {
        setSelectedInscriptions(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const selectCurrentMonth = () => {
        const estimatedAmount = (monthlyQuotas && monthlyQuotas[currentMonth - 1]) || selectedAthlete?.amount || 0;
        setSelectedPeriods([{ month: currentMonth, year: currentYear, amount: estimatedAmount }]);
        setModalYear(currentYear);
    };

    const selectAllMonths = () => {
        const allM = Array.from({ length: 12 }, (_, i) => ({
            month: i + 1,
            year: modalYear,
            amount: (monthlyQuotas && monthlyQuotas[i]) || selectedAthlete?.amount || 0
        }));
        setSelectedPeriods(allM);
    };

    const clearSelection = () => {
        setSelectedPeriods([]);
        setSelectedInscriptions([]);
    };

    // ── Dedicated Invoice Modal Handlers ──
    const openInvoiceModalForAthlete = async (athlete) => {
        setInvoiceModalAthlete(athlete);
        setInvoiceModalYear(currentYear);
        setInvoiceNif(athlete?.nif || '');
        setInvoiceName(athlete?.name || '');
        setInvoiceEmail(stripEmailAlias(athlete?.email || ''));
        setPaymentMethodName('Numerário');
        setInvoiceDeliveryMethod('both');
        setInvoiceModalInscriptions([]);

        const estimatedAmount = athlete?.amount || 0;
        setInvoiceModalPeriods([{ month: currentMonth, year: currentYear, amount: estimatedAmount }]);
        setIsInvoiceModalOpen(true);

        const token = localStorage.getItem('token');
        const apiUrl = `${API_BASE_URL}/api`;

        // If NIF or Email are not yet loaded on the athlete object, fetch user details directly
        if (!athlete?.nif || !athlete?.email) {
            try {
                const userRes = await fetch(`${apiUrl}/users/${athlete.userId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (userRes.ok) {
                    const userData = await userRes.json();
                    if (userData.nif && !athlete?.nif) setInvoiceNif(userData.nif);
                    if (userData.email && !athlete?.email) setInvoiceEmail(stripEmailAlias(userData.email));
                    if (userData.fullName && !athlete?.name) setInvoiceName(userData.fullName);
                }
            } catch (uErr) {
                console.error('Error fetching user profile for invoice modal:', uErr);
            }
        }

        try {
            const resQuotas = await fetch(`${apiUrl}/payment/admin/user-year-quotas?userId=${athlete.userId}&year=${currentYear}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (resQuotas.ok) {
                const quotasData = await resQuotas.json();
                if (quotasData && quotasData.quotas) {
                    setInvoiceModalMonthlyQuotas(quotasData.quotas);
                    const curMonthAmt = quotasData.quotas[currentMonth - 1] || athlete.amount || 0;
                    setInvoiceModalPeriods([{ month: currentMonth, year: currentYear, amount: curMonthAmt }]);
                }
            }
        } catch (e) {
            console.error('Error fetching quotas for invoice modal:', e);
        }
    };

    const toggleInvoiceModalPeriod = (m, y) => {
        setInvoiceModalPeriods(prev => {
            const exists = prev.find(p => p.month === m && p.year === y);
            if (exists) return prev.filter(p => !(p.month === m && p.year === y));
            const estimatedAmount = (invoiceModalMonthlyQuotas && invoiceModalMonthlyQuotas[m - 1]) || invoiceModalAthlete?.amount || 0;
            return [...prev, { month: m, year: y, amount: estimatedAmount }];
        });
    };

    const updateInvoiceModalPeriodAmount = (m, y, amt) => {
        const val = parseFloat(amt) || 0;
        setInvoiceModalPeriods(prev => prev.map(p =>
            (p.month === m && p.year === y) ? { ...p, amount: val } : p
        ));
    };

    const toggleInvoiceModalInscription = (id) => {
        setInvoiceModalInscriptions(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const selectInvoiceModalCurrentMonth = () => {
        const estimatedAmount = (invoiceModalMonthlyQuotas && invoiceModalMonthlyQuotas[currentMonth - 1]) || invoiceModalAthlete?.amount || 0;
        setInvoiceModalPeriods([{ month: currentMonth, year: currentYear, amount: estimatedAmount }]);
        setInvoiceModalYear(currentYear);
    };

    const selectInvoiceModalAllMonths = () => {
        setInvoiceModalPeriods(
            Array.from({ length: 12 }, (_, i) => ({
                month: i + 1,
                year: invoiceModalYear,
                amount: (invoiceModalMonthlyQuotas && invoiceModalMonthlyQuotas[i]) || invoiceModalAthlete?.amount || 0
            }))
        );
    };

    const clearInvoiceModalSelection = () => {
        setInvoiceModalPeriods([]);
        setInvoiceModalInscriptions([]);
    };

    const handleConfirmInvoiceModal = async () => {
        if (!invoiceModalAthlete) return;
        if (invoiceModalPeriods.length === 0 && invoiceModalInscriptions.length === 0) {
            alert('Por favor selecione pelo menos um mês ou inscrição para emitir a fatura.');
            return;
        }

        setIsSubmittingInvoice(true);
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const shouldSendEmail = invoiceDeliveryMethod === 'email' || invoiceDeliveryMethod === 'both';
            const body = {
                userId: invoiceModalAthlete.userId,
                periodYear: invoiceModalYear,
                status: "Completed",
                markInscriptionsPaid: invoiceModalInscriptions.length > 0 ? invoiceModalInscriptions : null,
                selectedPeriods: invoiceModalPeriods,
                issueInvoice: true,
                invoiceNif: invoiceNif?.trim() || '999999990',
                invoiceName: invoiceName?.trim() || invoiceModalAthlete.name,
                invoiceEmail: stripEmailAlias(invoiceEmail?.trim()) || null,
                paymentMethodName: paymentMethodName,
                invoicePeriods: invoiceModalPeriods,
                invoiceInscriptions: invoiceModalInscriptions,
                sendEmail: shouldSendEmail
            };

            const response = await fetch(`${apiUrl}/payment/admin/manual-payment`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || 'Falha ao emitir fatura');
            }

            const data = await response.json();
            setIsInvoiceModalOpen(false);
            fetchPaymentStatuses();

            if (data.invoice && data.invoice.success) {
                // If paper or both selected, open PDF in a new tab for immediate printing
                if ((invoiceDeliveryMethod === 'paper' || invoiceDeliveryMethod === 'both') && data.invoice.pdfUrl) {
                    window.open(data.invoice.pdfUrl, '_blank');
                }

                setInvoiceResult({
                    ...data.invoice,
                    athleteName: invoiceModalAthlete.name,
                    totalAmount: invoiceModalGrandTotal,
                    periodsCount: invoiceModalPeriods.length,
                    inscriptionsCount: invoiceModalInscriptions.length,
                    deliveryMethod: invoiceDeliveryMethod
                });
                setShowInvoiceSuccessModal(true);
            } else if (data.invoice && !data.invoice.success) {
                alert(`Pagamento regularizado, mas ocorreu um aviso no Moloni:\n${data.invoice.message}`);
            }
        } catch (error) {
            console.error('Error submitting invoice:', error);
            alert(error.message || 'Erro ao emitir fatura');
        } finally {
            setIsSubmittingInvoice(false);
        }
    };



    const handleExport = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;

            let url = `${apiUrl}/payment/admin/export-all-status?year=${currentYear}`;

            const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error('Failed to fetch export data');

            const blob = await response.blob();
            const urlBlob = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', urlBlob);
            link.setAttribute('download', `gestao_pagamentos_${currentYear}_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(urlBlob);
        } catch (error) {
            console.error('Error exporting payments:', error);
            alert('Erro ao exportar dados.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status, athlete = {}) => {
        const s = status || '';
        const isSenior = athlete.team?.toLowerCase().includes('senior') ||
            athlete.sport?.toLowerCase().includes('senior') ||
            athlete.team?.toLowerCase().includes('seniores') ||
            athlete.sport?.toLowerCase().includes('seniores');

        switch (s) {
            case 'Paid': case 'Regularizada': case 'Completed':
                return <span className="status-badge paid"><CheckCircle2 size={12} /> Pago</span>;
            case 'Pendente': case 'Pending':
                return <span className="status-badge pending"><AlertCircle size={12} /> Pendente</span>;
            case 'Unpaid': case 'Failed':
                if (isSenior) return <span className="status-badge senior"><User size={12} /> (senior)</span>;
                return <span className="status-badge unpaid"><X size={12} /> Não Pago</span>;
            default:
                return <span className="status-badge">{s}</span>;
        }
    };

    const getPageNumbers = () => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        const set = new Set([1, totalPages, page, page - 1, page + 1].filter(p => p >= 1 && p <= totalPages));
        const sorted = [...set].sort((a, b) => a - b);
        const result = [];
        sorted.forEach((p, idx) => {
            if (idx > 0 && p - sorted[idx - 1] > 1) result.push('...');
            result.push(p);
        });
        return result;
    };

    const firstRecord = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
    const lastRecord = Math.min(page * pageSize, totalCount);

    const MONTHS_PT = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];

    const activeModalInscriptions = modalInscriptions.length > 0 ? modalInscriptions : (selectedAthlete?.pendingInscriptions || []);
    const activeInvoiceModalInscriptions = invoiceModalInscriptionsList.length > 0 ? invoiceModalInscriptionsList : (invoiceModalAthlete?.pendingInscriptions || []);

    const monthsTotal = selectedPeriods.reduce((acc, p) => acc + (p.amount || 0), 0);
    const inscriptionsTotal = activeModalInscriptions
        .filter(i => selectedInscriptions.includes(i.athleteTeamId))
        .reduce((acc, i) => acc + i.amount, 0) || 0;
    const grandTotal = monthsTotal + inscriptionsTotal;

    const invoiceModalMonthsTotal = invoiceModalPeriods.reduce((acc, p) => acc + (p.amount || 0), 0);
    const invoiceModalInscriptionsTotal = activeInvoiceModalInscriptions
        .filter(i => invoiceModalInscriptions.includes(i.athleteTeamId))
        .reduce((acc, i) => acc + i.amount, 0) || 0;
    const invoiceModalGrandTotal = invoiceModalMonthsTotal + invoiceModalInscriptionsTotal;

    const hasInscriptions = activeModalInscriptions.length > 0;
    const isAthleteAnnual = selectedAthlete?.paymentPreference === 'Annual';



    const confirmButtonLabel = () => {
        if (selectedAthlete?.status === 'Paid' || selectedAthlete?.status === 'Regularizada' || selectedAthlete?.status === 'Completed') return 'Guardar Alterações';
        if (isAthleteAnnual && selectedPeriods.length === 12) {
            const parts = ['Ano'];
            if (selectedInscriptions.length > 0)
                parts.push(`${selectedInscriptions.length} ${selectedInscriptions.length === 1 ? 'Inscrição' : 'Inscrições'}`);
            return `Validar ${parts.join(' + ')}`;
        }
        const parts = [];
        if (selectedPeriods.length > 0)
            parts.push(`${selectedPeriods.length} ${selectedPeriods.length === 1 ? 'Mês' : 'Meses'}`);
        if (selectedInscriptions.length > 0)
            parts.push(`${selectedInscriptions.length} ${selectedInscriptions.length === 1 ? 'Inscrição' : 'Inscrições'}`);
        return parts.length > 0 ? `Validar ${parts.join(' + ')}` : 'Validar';
    };

    return (
        <div className="payment-manager">
            {/* ── Header ── */}
            <div className="manager-header">
                <div className="header-content">
                    <h1>Gestão de Quotas</h1>
                    <p className="header-subtitle">Monitorize e gira os pagamentos dos atletas</p>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-daily-invoices"
                        onClick={() => setIsDailyInvoicesModalOpen(true)}
                        title="Consultar faturas emitidas por dia no Moloni"
                    >
                        <Receipt size={18} /> <span>Faturas do Dia</span>
                    </button>
                    <button className="btn-export" onClick={handleExport} disabled={loading} title="Exportar para Excel">
                        <FileSpreadsheet size={18} /> <span>Exportar Excel</span>
                    </button>
                    <div className="period-selector">
                        <select value={currentMonth} onChange={(e) => setCurrentMonth(parseInt(e.target.value))} className="filter-select month-select">
                            {MONTHS_PT.map((m, i) => (
                                <option key={i + 1} value={i + 1}>{m}</option>
                            ))}
                        </select>
                        <select value={currentYear} onChange={(e) => setCurrentYear(parseInt(e.target.value))} className="filter-select year-select">
                            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* ── Filters ── */}
            <div className="filters-card">
                <div className="filters-container">
                    <div className="filter-group search-group">
                        <div className="filter-header">
                            <label className="filter-label">Pesquisa</label>
                        </div>
                        <div className="search-wrapper">
                            <Search className="search-icon" size={18} />
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Nome, email, NIF ou Nº Sócio..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')}><X size={14} /></button>
                            )}
                        </div>
                    </div>

                    <div className="filter-group">
                        <div className="filter-header">
                            <label className="filter-label">Estado</label>
                        </div>
                        <select
                            className="filter-select"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">Todos os Estados</option>
                            <option value="Paid">Pago</option>
                            <option value="Pending">Pendente</option>
                            <option value="Unpaid">Não Pago</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <div className="filter-header">
                            <label className="filter-label">Equipa</label>
                        </div>
                        <select
                            className="filter-select"
                            value={filterTeam}
                            onChange={(e) => setFilterTeam(e.target.value)}
                        >
                            <option value="all">Todas as Equipas</option>
                            <option value="coach">Treinadores</option>
                            <option value="-1">Sócio</option>
                            <option value="-2">Com Equipa</option>
                            {teams.map(team => (
                                <option key={team.id} value={team.id}>{team.name} ({team.sportName})</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <div className="filter-header">
                            <label className="filter-label">Modalidade</label>
                        </div>
                        <select
                            className="filter-select"
                            value={filterSport}
                            onChange={(e) => setFilterSport(e.target.value)}
                        >
                            <option value="all">Todas as Modalidades</option>
                            {sports.map(sport => <option key={sport.id} value={sport.id}>{sport.name}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* ── Table Stats ── */}
            <div className="table-stats">
                <span className="records-info">
                    {totalCount === 0
                        ? 'Nenhum registo encontrado'
                        : <>A mostrar <strong>{firstRecord}–{lastRecord}</strong> de <strong>{totalCount}</strong> registos</>
                    }
                </span>
                <div className="page-size-selector">
                    <label className="page-size-label">Registos por página:</label>
                    <div className="page-size-input-wrapper">
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(parseInt(e.target.value))}
                            className="page-size-select"
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <ChevronDown className="select-icon" size={14} />
                    </div>
                </div>
            </div>

            {/* ── Table ── */}
            <div className="users-table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th><div className="th-content">Nº Sócio</div></th>
                            <th><div className="th-content">Nome</div></th>
                            <th><div className="th-content">Equipa / Modalidade</div></th>
                            <th><div className="th-content">Período</div></th>
                            <th><div className="th-content">Valor</div></th>
                            <th><div className="th-content">Estado</div></th>
                            <th className="payment-actions-cell">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <tr key={i} className="skeleton-row">
                                    <td colSpan="7"><div className="skeleton-bar"></div></td>
                                </tr>
                            ))
                        ) : (athletes?.length || 0) > 0 ? (
                            athletes.map((athlete) => (
                                <tr key={athlete.userId} className={`row-status-${(athlete.status || '').toLowerCase()}`}>
                                    <td data-label="Nº Sócio">
                                        {editingMembershipId === athlete.userId ? (
                                            <div className="membership-edit-container">
                                                <input
                                                    className="membership-input"
                                                    value={editingMembershipValue}
                                                    onChange={e => setEditingMembershipValue(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') confirmEditMembership(athlete.userId);
                                                        if (e.key === 'Escape') cancelEditMembership();
                                                    }}
                                                    autoFocus
                                                    maxLength={20}
                                                />
                                                <div className="membership-edit-actions">
                                                    <button className="membership-btn-confirm" title="Confirmar" onClick={() => confirmEditMembership(athlete.userId)}><CheckCircle2 size={16} /></button>
                                                    <button className="membership-btn-cancel" title="Cancelar" onClick={cancelEditMembership}><X size={14} /></button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="membership-view-container">
                                                <div className="membership-number-wrapper">
                                                    <span className="socio-badge">{athlete.membershipNumber}</span>
                                                    <button className="membership-edit-btn" title="Editar Nº Sócio" onClick={() => startEditMembership(athlete)}><Edit2 size={14} /></button>
                                                </div>
                                                {membershipDuplicates[athlete.userId] && (
                                                    <div className="membership-duplicate-warning" title={`Nº duplicado: ${membershipDuplicates[athlete.userId].name}`}>
                                                        <AlertTriangle size={14} />
                                                        <span>Duplicado: <strong>{membershipDuplicates[athlete.userId].name}</strong></span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td data-label="Nome">
                                        <span className="user-name">{athlete.name}</span>
                                    </td>
                                    <td data-label="Equipa / Modalidade">
                                        <div className="team-sport-cell">
                                            {athlete.isCoach && <span className="coach-prefix">Treinador - </span>}
                                            {athlete.team?.split(', ').map((t, idx) => (
                                                <span key={idx} className="team-name">{t}</span>
                                            ))}
                                            <span className="sport-name">{athlete.sport}</span>
                                        </div>
                                    </td>
                                    <td data-label="Período">
                                        <div className="period-cell">
                                            <span className="period-value">{athlete.currentPeriod}</span>
                                            <span className="period-type">{athlete.paymentPreference === 'Annual' ? 'Anual' : 'Mensal'}</span>
                                        </div>
                                    </td>
                                    <td data-label="Valor" className="amount-cell">
                                        {(() => {
                                            const hasCustomQuota = athlete.customQuotaPrice != null &&
                                                athlete.customQuotaPrice !== '' &&
                                                !isNaN(Number(athlete.customQuotaPrice));
                                            return (
                                                <div className="amount-wrapper">
                                                    {editingQuotaUserId === athlete.userId ? (
                                                        <div className="quota-edit-container" onClick={e => e.stopPropagation()}>
                                                            <input
                                                                className="quota-input"
                                                                type="number"
                                                                step="0.01"
                                                                placeholder="Auto"
                                                                value={editingQuotaValue}
                                                                onChange={e => setEditingQuotaValue(e.target.value)}
                                                                onKeyDown={e => {
                                                                    if (e.key === 'Enter') confirmEditQuota(athlete.userId);
                                                                    if (e.key === 'Escape') cancelEditQuota();
                                                                }}
                                                                autoFocus
                                                            />
                                                            <div className="quota-edit-actions">
                                                                <button
                                                                    type="button"
                                                                    className="quota-action-btn save"
                                                                    title="Guardar valor"
                                                                    onClick={() => confirmEditQuota(athlete.userId)}
                                                                >
                                                                    <Check size={14} />
                                                                </button>
                                                                {hasCustomQuota && (
                                                                    <button
                                                                        type="button"
                                                                        className="quota-action-btn reset"
                                                                        title="Restaurar cálculo automático (limpar quota personalizada)"
                                                                        onClick={() => handleResetCustomQuota(athlete.userId, athlete.name)}
                                                                    >
                                                                        <RotateCcw size={13} />
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="quota-action-btn cancel"
                                                                    title="Cancelar"
                                                                    onClick={cancelEditQuota}
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="quota-display-container">
                                                            <div className="quota-price-wrapper">
                                                                <span
                                                                    className={`monthly-fee ${hasCustomQuota ? 'custom-price' : ''}`}
                                                                    onClick={(e) => fetchBreakdown(athlete.userId, e)}
                                                                    title="Clica para ver o detalhe do cálculo"
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    {((athlete.status === 'Paid' || athlete.status === 'Regularizada' || athlete.status === 'Completed') && athlete.amountPaid !== null && athlete.amountPaid !== undefined)
                                                                        ? `${athlete.amountPaid.toFixed(2)}€`
                                                                        : `${(athlete.amount || 0).toFixed(2)}€`}
                                                                </span>
                                                                {hasCustomQuota && (
                                                                    <span className="custom-quota-badge" title="Quota personalizada definida manualmente">
                                                                        Manual
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="quota-actions">
                                                                <button
                                                                    className="quota-icon-btn edit"
                                                                    title="Editar quota"
                                                                    onClick={() => startEditQuota(athlete)}
                                                                >
                                                                    <Edit2 size={13} />
                                                                </button>
                                                                {hasCustomQuota && (
                                                                    <button
                                                                        className="quota-icon-btn reset"
                                                                        title="Restaurar cálculo automático do sistema"
                                                                        onClick={() => handleResetCustomQuota(athlete.userId, athlete.name)}
                                                                    >
                                                                        <RotateCcw size={13} />
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {breakdownPopover.userId === athlete.userId && ReactDOM.createPortal(
                                                                <div
                                                                    className="breakdown-popover"
                                                                    onClick={e => e.stopPropagation()}
                                                                    style={{
                                                                        position: 'fixed',
                                                                        top: breakdownPopover.placement === 'top' ? 'auto' : `${breakdownPopover.y}px`,
                                                                        bottom: breakdownPopover.placement === 'top' ? `${breakdownPopover.y}px` : 'auto',
                                                                        left: `${breakdownPopover.x}px`,
                                                                        maxHeight: 'min(420px, 80vh)',
                                                                        overflowY: 'auto',
                                                                        zIndex: 9999
                                                                    }}
                                                                >
                                                                    <div className="breakdown-popover-header">
                                                                        <span>Detalhe — {MONTHS_PT[currentMonth - 1]} {currentYear}</span>
                                                                        <button onClick={() => setBreakdownPopover({ userId: null, data: null, loading: false, x: 0, y: 0, placement: 'bottom' })}>✕</button>
                                                                    </div>
                                                                    {breakdownPopover.loading ? (
                                                                        <div className="breakdown-loading">A calcular...</div>
                                                                    ) : breakdownPopover.data ? (
                                                                        <>
                                                                            {(hasCustomQuota || breakdownPopover.data.breakdown?.some(b => b.label === 'Quota Customizada')) && (
                                                                                <div className="breakdown-custom-notice">
                                                                                    <div className="breakdown-custom-text">
                                                                                        <span>⚙️ Quota Personalizada</span>
                                                                                        <small>Valor fixo definido manualmente</small>
                                                                                    </div>
                                                                                    <button
                                                                                        className="breakdown-reset-btn"
                                                                                        onClick={() => handleResetCustomQuota(athlete.userId, athlete.name)}
                                                                                        title="Remover quota personalizada e voltar ao cálculo automático"
                                                                                    >
                                                                                        <RotateCcw size={12} /> Restaurar Auto
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                            <ul className="breakdown-list">
                                                                                {breakdownPopover.data.breakdown?.map((item, i) => (
                                                                                    <li key={i} className={`breakdown-item ${item.isDiscount ? 'breakdown-discount' : ''}`}>
                                                                                        <span className="breakdown-label">{item.label}</span>
                                                                                        <span className="breakdown-amount">{item.amount >= 0 ? '+' : ''}{item.amount.toFixed(2)}€</span>
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                            <div className="breakdown-total">
                                                                                <span>Total</span>
                                                                                <span><strong>{breakdownPopover.data.monthlyQuota?.toFixed(2)}€</strong></span>
                                                                            </div>
                                                                            {breakdownPopover.data.discountsApplied?.length > 0 && (
                                                                                <div className="breakdown-discounts">
                                                                                    {breakdownPopover.data.discountsApplied.map(d => (
                                                                                        <span key={d} className="breakdown-discount-tag">{d}</span>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                            {breakdownPopover.data.parentsPaymentWarning && (
                                                                                <div className="breakdown-warning">⚠️ Pais não pagaram — desconto de menor não aplicado</div>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <div className="breakdown-loading">Erro ao carregar.</div>
                                                                    )}
                                                                </div>,
                                                                document.body
                                                            )}
                                                        </div>
                                                    )}
                                                    {athlete.pendingInscriptions?.length > 0 && (
                                                        <div className="pending-inscription-tag" title={`Inscrições pendentes: ${athlete.pendingInscriptions.map(i => i.sportName).join(', ')}`}>
                                                            +{athlete.pendingInscriptions.reduce((acc, i) => acc + i.amount, 0).toFixed(2)}€ Inscr.
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    <td data-label="Estado">{getStatusBadge(athlete.status, athlete)}</td>
                                    <td className="payment-actions-cell">
                                        <div className="payment-action-buttons">
                                            <button
                                                className="payment-btn-action payment-btn-profile"
                                                title="Ver Ficha / Histórico"
                                                onClick={() => { setSelectedUserId(athlete.userId); setIsHistoryModalOpen(true); }}
                                            >
                                                <User size={16} /> <span>Ficha</span>
                                            </button>
                                            <button
                                                className="payment-btn-action payment-btn-profile"
                                                title="Editar ficha do sócio"
                                                onClick={() => setEditMemberUserId(athlete.userId)}
                                            >
                                                <Edit2 size={16} /> <span>Sócio</span>
                                            </button>
                                            {athlete.status !== 'Paid' && athlete.status !== 'Regularizada' && athlete.status !== 'Completed' ? (
                                                <button className="payment-btn-action payment-btn-validate" title="Validar Pagamento" onClick={() => handleUpdateStatus(athlete.userId, "Completed")}>
                                                    <CheckCircle2 size={16} /> <span>Validar</span>
                                                </button>
                                            ) : (
                                                <button className="payment-btn-action payment-btn-validate" title="Editar Pagamento" onClick={() => handleUpdateStatus(athlete.userId, "Completed")}>
                                                    <Edit2 size={16} /> <span>Editar</span>
                                                </button>
                                            )}
                                            <button
                                                className="payment-btn-action payment-btn-invoice"
                                                title="Emitir Fatura-Recibo"
                                                onClick={() => openInvoiceModalForAthlete(athlete)}
                                            >
                                                <Receipt size={16} /> <span>Fatura</span>
                                            </button>
                                            <button
                                                className="payment-btn-action payment-btn-withdraw"
                                                title="Marcar como Desistente"
                                                onClick={() => handleMarkWithdrawn(athlete)}
                                            >
                                                <UserMinus size={16} />
                                            </button>
                                        </div>

                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="empty-message">
                                    <div className="empty-state">
                                        <X size={64} className="empty-icon" />
                                        <p>Nenhum registo encontrado para os filtros selecionados.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        className="pagination-btn"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                    >
                        <ChevronLeft size={16} /> Anterior
                    </button>

                    <div className="pagination-pages" style={{ display: 'flex', gap: '8px' }}>
                        {getPageNumbers().map((p, idx) => (
                            p === '...' ? (
                                <span key={`el-${idx}`} className="page-ellipsis">...</span>
                            ) : (
                                <button
                                    key={idx}
                                    className={`page-number ${page === p ? 'active' : ''}`}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </button>
                            )
                        ))}
                    </div>

                    <button
                        className="pagination-btn"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                    >
                        Seguinte <ChevronRight size={16} />
                    </button>
                </div>
            )}

            {/* ── Month Selection Modal for Validation ── */}
            {isMonthModalOpen && (
                <div className="admin-modal-overlay" onClick={() => setIsMonthModalOpen(false)}>
                    <div className="admin-modal months-modal refined" onClick={e => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div className="modal-header">
                            <div className="modal-header-left">
                                <div className="modal-header-icon">
                                    <Calendar size={20} />
                                </div>
                                <div className="modal-header-left-text">
                                    <h3>Validar Quotas</h3>
                                    <p className="modal-subtitle">
                                        {selectedAthlete?.name}
                                        {isAthleteAnnual && <span className="annual-indicator"> (Quota Anual)</span>}
                                    </p>
                                </div>
                            </div>
                            <button className="close-btn" onClick={() => setIsMonthModalOpen(false)}><X size={20} /></button>
                        </div>

                        <div className="modal-body">
                            {/* Controls Row: Year + Quick Actions */}
                            <div className="modal-controls-row">
                                <div className="modal-year-pill">
                                    <Calendar size={18} />
                                    <button className="year-nav-btn" onClick={() => setModalYear(y => y - 1)}><ChevronLeft size={16} /></button>
                                    <span className="year-display">{modalYear}</span>
                                    <button className="year-nav-btn" onClick={() => setModalYear(y => y + 1)}><ChevronRight size={16} /></button>
                                    {modalYear !== currentYear && (
                                        <button className="year-reset-mini" onClick={() => setModalYear(currentYear)} title={`Voltar a ${currentYear}`}><Undo2 size={14} /></button>
                                    )}
                                </div>

                                <div className="modal-quick-actions">
                                    <button className="quick-action-btn" onClick={selectCurrentMonth}>
                                        <Zap size={14} /> Mês Corrente
                                    </button>
                                    <button className="quick-action-btn" onClick={selectAllMonths}><List size={14} /> Todos</button>
                                    <button className="quick-action-btn quick-action-btn--clear" onClick={clearSelection} disabled={selectedPeriods.length === 0 && selectedInscriptions.length === 0}><X size={14} /> Limpar</button>
                                </div>
                            </div>

                            {/* Month Grid */}
                            <div className="months-grid-v2">
                                {Array.from({ length: 12 }, (_, i) => {
                                    const m = i + 1;
                                    const period = selectedPeriods.find(p => p.month === m && p.year === modalYear);
                                    const isSelected = !!period;
                                    const monthLabel = MONTHS_PT[i].substring(0, 3);

                                    return (
                                        <div
                                            key={m}
                                            className={`month-tile ${isSelected ? 'month-tile--selected' : ''}`}
                                        >
                                            <button
                                                className="month-tile-toggle"
                                                onClick={() => togglePeriod(m, modalYear)}
                                                type="button"
                                            >
                                                <div className={`month-checkbox ${isSelected ? 'month-checkbox--checked' : ''}`}>
                                                    {isSelected && <CheckCircle2 size={16} />}
                                                </div>
                                                <span className="month-tile-label">{monthLabel}</span>
                                            </button>

                                            {isSelected ? (
                                                <div className="month-tile-amount">
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={period.amount}
                                                        onChange={(e) => updatePeriodAmount(m, modalYear, e.target.value)}
                                                        className="month-amount-input"
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                    <span className="month-amount-currency">€</span>
                                                </div>
                                            ) : (
                                                <div className="month-tile-amount month-tile-amount--estimated">
                                                    <span className="month-amount-value">
                                                        {((monthlyQuotas && monthlyQuotas[m - 1]) || selectedAthlete?.amount || 0).toFixed(2)}
                                                    </span>
                                                    <span className="month-amount-currency">€</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Pending Inscriptions */}
                            {hasInscriptions && (
                                <div className="modal-inscriptions-section">
                                    <div className="inscriptions-section-header">
                                        <span className="inscriptions-section-title">Inscrições Pendentes</span>
                                        <span className="inscriptions-count-badge">
                                            {activeModalInscriptions.length}
                                        </span>
                                    </div>
                                    <div className="inscriptions-list-v2">
                                        {activeModalInscriptions.map(ins => {
                                            const isSelected = selectedInscriptions.includes(ins.athleteTeamId);
                                            return (
                                                <button
                                                    key={ins.athleteTeamId}
                                                    className={`inscription-tile ${isSelected ? 'inscription-tile--selected' : ''}`}
                                                    onClick={() => toggleInscription(ins.athleteTeamId)}
                                                    type="button"
                                                >
                                                    <div className={`month-checkbox ${isSelected ? 'month-checkbox--checked' : ''}`}>
                                                        {isSelected && <CheckCircle2 size={16} />}
                                                    </div>
                                                    <span className="inscription-sport">{ins.sportName}</span>
                                                    <span className="inscription-amount">{ins.amount.toFixed(2)}€</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}


                        </div>

                        <div className="modal-footer-v2">
                            <div className="modal-footer-actions">
                                <button
                                    className="btn-cancel"
                                    onClick={() => setIsMonthModalOpen(false)}
                                    disabled={isSubmittingPayment}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="btn-confirm"
                                    onClick={handleConfirmModalPayment}
                                    disabled={isSubmittingPayment || (selectedPeriods.length === 0 && selectedInscriptions.length === 0)}
                                >
                                    {isSubmittingPayment ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            <span>A validar...</span>
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 size={16} />
                                            <span>{confirmButtonLabel()}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* ── Dedicated Invoice Issuance Modal ── */}
            {isInvoiceModalOpen && invoiceModalAthlete && (
                <div className="admin-modal-overlay" onClick={() => setIsInvoiceModalOpen(false)}>
                    <div className="admin-modal months-modal refined invoice-modal" onClick={e => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div className="modal-header">
                            <div className="modal-header-left">
                                <div className="modal-header-icon invoice-header-icon">
                                    <Receipt size={20} />
                                </div>
                                <div className="modal-header-left-text">
                                    <h3>Emitir Fatura-Recibo</h3>
                                    <p className="modal-subtitle">
                                        {invoiceModalAthlete.name} &bull; Nº {invoiceModalAthlete.membershipNumber}
                                    </p>
                                </div>
                            </div>
                            <button className="close-btn" onClick={() => setIsInvoiceModalOpen(false)}><X size={20} /></button>
                        </div>

                        <div className="modal-body">
                            {/* Year + Quick Actions */}
                            <div className="modal-controls-row">
                                <div className="modal-year-pill">
                                    <Calendar size={18} />
                                    <button className="year-nav-btn" onClick={() => setInvoiceModalYear(y => y - 1)}><ChevronLeft size={16} /></button>
                                    <span className="year-display">{invoiceModalYear}</span>
                                    <button className="year-nav-btn" onClick={() => setInvoiceModalYear(y => y + 1)}><ChevronRight size={16} /></button>
                                    {invoiceModalYear !== currentYear && (
                                        <button className="year-reset-mini" onClick={() => setInvoiceModalYear(currentYear)} title={`Voltar a ${currentYear}`}><Undo2 size={14} /></button>
                                    )}
                                </div>

                                <div className="modal-quick-actions">
                                    <button className="quick-action-btn" onClick={selectInvoiceModalCurrentMonth}>
                                        <Zap size={14} /> Mês Corrente
                                    </button>
                                    <button className="quick-action-btn" onClick={selectInvoiceModalAllMonths}>
                                        <List size={14} /> Todos
                                    </button>
                                    <button
                                        className="quick-action-btn quick-action-btn--clear"
                                        onClick={clearInvoiceModalSelection}
                                        disabled={invoiceModalPeriods.length === 0 && invoiceModalInscriptions.length === 0}
                                    >
                                        <X size={14} /> Limpar
                                    </button>
                                </div>
                            </div>

                            {/* Compact Month Grid (4x3) */}
                            <div className="months-grid-v2">
                                {Array.from({ length: 12 }, (_, i) => {
                                    const m = i + 1;
                                    const period = invoiceModalPeriods.find(p => p.month === m && p.year === invoiceModalYear);
                                    const isSelected = !!period;
                                    const monthLabel = MONTHS_PT[i].substring(0, 3);

                                    return (
                                        <div
                                            key={m}
                                            className={`month-tile ${isSelected ? 'month-tile--selected' : ''}`}
                                        >
                                            <button
                                                className="month-tile-toggle"
                                                onClick={() => toggleInvoiceModalPeriod(m, invoiceModalYear)}
                                                type="button"
                                            >
                                                <div className={`month-checkbox ${isSelected ? 'month-checkbox--checked' : ''}`}>
                                                    {isSelected && <CheckCircle2 size={16} />}
                                                </div>
                                                <span className="month-tile-label">{monthLabel}</span>
                                            </button>

                                            {isSelected ? (
                                                <div className="month-tile-amount">
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={period.amount}
                                                        onChange={(e) => updateInvoiceModalPeriodAmount(m, invoiceModalYear, e.target.value)}
                                                        className="month-amount-input"
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                    <span className="month-amount-currency">€</span>
                                                </div>
                                            ) : (
                                                <div className="month-tile-amount month-tile-amount--estimated">
                                                    <span className="month-amount-value">
                                                        {((invoiceModalMonthlyQuotas && invoiceModalMonthlyQuotas[m - 1]) || invoiceModalAthlete?.amount || 0).toFixed(2)}
                                                    </span>
                                                    <span className="month-amount-currency">€</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Pending Inscriptions */}
                            {(activeInvoiceModalInscriptions.length || 0) > 0 && (
                                <div className="modal-inscriptions-section">
                                    <div className="inscriptions-section-header">
                                        <span className="inscriptions-section-title">Inscrições</span>
                                        <span className="inscriptions-count-badge">
                                            {activeInvoiceModalInscriptions.length}
                                        </span>
                                    </div>
                                    <div className="inscriptions-list-v2">
                                        {activeInvoiceModalInscriptions.map(ins => {
                                            const isSelected = invoiceModalInscriptions.includes(ins.athleteTeamId);
                                            return (
                                                <button
                                                    key={ins.athleteTeamId}
                                                    className={`inscription-tile ${isSelected ? 'inscription-tile--selected' : ''}`}
                                                    onClick={() => toggleInvoiceModalInscription(ins.athleteTeamId)}
                                                    type="button"
                                                >
                                                    <div className={`month-checkbox ${isSelected ? 'month-checkbox--checked' : ''}`}>
                                                        {isSelected && <CheckCircle2 size={16} />}
                                                    </div>
                                                    <span className="inscription-sport">{ins.sportName}</span>
                                                    <span className="inscription-amount">{ins.amount.toFixed(2)}€</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Fiscal Data Form */}
                            <div className="modal-invoicing-section active-invoice-form">
                                <div className="invoicing-section-header">
                                    <div className="invoicing-header-title">
                                        <div className="invoicing-icon-badge">
                                            <Receipt size={18} />
                                        </div>
                                        <div>
                                            <span className="invoicing-title-text">Dados Fiscais para a Fatura</span>
                                            <p className="invoicing-subtitle-text">Documento fiscal certificado com envio para a AT via Moloni</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="invoicing-form-grid">
                                    <div className="invoicing-field">
                                        <div className="invoicing-field-header">
                                            <label>NIF do Titular</label>
                                            <button
                                                type="button"
                                                className="btn-quick-nif"
                                                onClick={() => setInvoiceNif('999999990')}
                                                title="Definir como Consumidor Final"
                                            >
                                                Consumidor Final
                                            </button>
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="ex: 123456789 ou 999999990"
                                            value={invoiceNif}
                                            onChange={(e) => setInvoiceNif(e.target.value)}
                                            className="invoicing-input"
                                        />
                                    </div>

                                    <div className="invoicing-field">
                                        <label>Nome na Fatura</label>
                                        <input
                                            type="text"
                                            placeholder="Nome do titular"
                                            value={invoiceName}
                                            onChange={(e) => setInvoiceName(e.target.value)}
                                            className="invoicing-input"
                                        />
                                    </div>

                                    <div className="invoicing-field">
                                        <label>Forma de Pagamento</label>
                                        <select
                                            value={paymentMethodName}
                                            onChange={(e) => setPaymentMethodName(e.target.value)}
                                            className="invoicing-select"
                                        >
                                            <option value="Numerário">Numerário / Dinheiro</option>
                                            <option value="Multibanco">TPA / Multibanco</option>
                                            <option value="MBWay">MBWay</option>
                                            <option value="Transferência Bancária">Transferência Bancária</option>
                                        </select>
                                    </div>

                                    <div className="invoicing-field">
                                        <label>Email (Opcional)</label>
                                        <input
                                            type="email"
                                            placeholder="ex: socio@email.com"
                                            value={invoiceEmail}
                                            onChange={(e) => setInvoiceEmail(e.target.value)}
                                            className="invoicing-input"
                                        />
                                    </div>

                                    <div className="invoicing-field" style={{ gridColumn: '1 / -1' }}>
                                        <div className="invoicing-field-header">
                                            <label>Destino da Fatura</label>
                                            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                                {invoiceDeliveryMethod === 'both' ? 'Envia por email e abre PDF para impressão' :
                                                 invoiceDeliveryMethod === 'email' ? 'Apenas envio por email' : 'Abre o PDF em nova aba para impressão'}
                                            </span>
                                        </div>
                                        <div className="invoice-delivery-selector">
                                            <button
                                                type="button"
                                                className={`delivery-option-btn ${invoiceDeliveryMethod === 'both' ? 'active' : ''}`}
                                                onClick={() => setInvoiceDeliveryMethod('both')}
                                            >
                                                <div className="delivery-icon-group">
                                                    <Mail size={16} />
                                                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>+</span>
                                                    <Printer size={16} />
                                                </div>
                                                <div className="delivery-label-group">
                                                    <span className="delivery-title">Ambos</span>
                                                    <span className="delivery-desc">Email + Abrir PDF</span>
                                                </div>
                                            </button>

                                            <button
                                                type="button"
                                                className={`delivery-option-btn ${invoiceDeliveryMethod === 'email' ? 'active' : ''}`}
                                                onClick={() => setInvoiceDeliveryMethod('email')}
                                            >
                                                <Mail size={18} />
                                                <div className="delivery-label-group">
                                                    <span className="delivery-title">Email</span>
                                                    <span className="delivery-desc">Apenas por email</span>
                                                </div>
                                            </button>

                                            <button
                                                type="button"
                                                className={`delivery-option-btn ${invoiceDeliveryMethod === 'paper' ? 'active' : ''}`}
                                                onClick={() => setInvoiceDeliveryMethod('paper')}
                                            >
                                                <Printer size={18} />
                                                <div className="delivery-label-group">
                                                    <span className="delivery-title">Papel</span>
                                                    <span className="delivery-desc">Apenas imprimir</span>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer-v2">
                            <div className="modal-totals">
                                {invoiceModalPeriods.length > 0 && (
                                    <div className="total-row">
                                        <span className="total-label">Quotas ({invoiceModalPeriods.length} {invoiceModalPeriods.length === 1 ? 'mês' : 'meses'})</span>
                                        <span className="total-value">{invoiceModalMonthsTotal.toFixed(2)}€</span>
                                    </div>
                                )}
                                {invoiceModalInscriptionsTotal > 0 && (
                                    <div className="total-row">
                                        <span className="total-label">Inscrições</span>
                                        <span className="total-value">{invoiceModalInscriptionsTotal.toFixed(2)}€</span>
                                    </div>
                                )}
                                <div className="total-row total-row--grand total-row--invoice">
                                    <span className="total-label">Total da Fatura</span>
                                    <span className="total-value invoice-highlight-value">{invoiceModalGrandTotal.toFixed(2)}€</span>
                                </div>
                            </div>
                            <div className="modal-footer-actions">
                                <button
                                    className="btn-cancel"
                                    onClick={() => setIsInvoiceModalOpen(false)}
                                    disabled={isSubmittingInvoice}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="btn-confirm btn-invoice-confirm"
                                    onClick={handleConfirmInvoiceModal}
                                    disabled={isSubmittingInvoice || (invoiceModalPeriods.length === 0 && invoiceModalInscriptions.length === 0)}
                                >
                                    {isSubmittingInvoice ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            <span>A emitir fatura...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Receipt size={16} />
                                            <span>Emitir Fatura ({invoiceModalGrandTotal.toFixed(2)}€)</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Invoice Generation Success Modal ── */}
            {showInvoiceSuccessModal && invoiceResult && (
                <div className="admin-modal-overlay" onClick={() => setShowInvoiceSuccessModal(false)}>
                    <div className="admin-modal invoice-success-modal" onClick={e => e.stopPropagation()}>
                        <div className="invoice-success-header">
                            <div className="invoice-success-icon-badge">
                                <CheckCircle2 size={36} />
                            </div>
                            <h3>Pagamento Regularizado!</h3>
                            <p className="invoice-success-subtitle">
                                {invoiceResult.message || 'O pagamento foi registado e a fatura emitida.'}
                            </p>
                        </div>

                        <div className="invoice-success-details">
                            <div className="invoice-detail-row">
                                <span className="invoice-detail-label">Sócio / Titular:</span>
                                <span className="invoice-detail-value">{invoiceResult.athleteName || selectedAthlete?.name}</span>
                            </div>
                            <div className="invoice-detail-row">
                                <span className="invoice-detail-label">Valor Total:</span>
                                <span className="invoice-detail-value invoice-detail-amount">{invoiceResult.totalAmount?.toFixed(2)} €</span>
                            </div>
                            {invoiceResult.documentNumber && (
                                <div className="invoice-detail-row">
                                    <span className="invoice-detail-label">N.º Fatura-Recibo:</span>
                                    <span className="invoice-detail-value invoice-doc-badge">{invoiceResult.documentNumber}</span>
                                </div>
                            )}
                            {invoiceResult.deliveryMethod && (
                                <div className="invoice-detail-row">
                                    <span className="invoice-detail-label">Destino:</span>
                                    <span className="invoice-detail-value" style={{ fontWeight: '600' }}>
                                        {invoiceResult.deliveryMethod === 'both' ? '✉️ Email + 🖨️ Papel (Aberto)' :
                                         invoiceResult.deliveryMethod === 'email' ? '✉️ Enviado por Email' : '🖨️ Impressão em Papel (Aberto)'}
                                    </span>
                                </div>
                            )}
                            {invoiceResult.emailSentTo && (
                                <div className="invoice-detail-row">
                                    <span className="invoice-detail-label">Email Enviado:</span>
                                    <span className="invoice-detail-value" style={{ color: '#0284c7', fontWeight: '600' }}>
                                        ✉️ {invoiceResult.emailSentTo}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="invoice-success-actions">
                            {invoiceResult.pdfUrl ? (
                                <a
                                    href={invoiceResult.pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-print-invoice"
                                >
                                    <ExternalLink size={18} />
                                    <span>Abrir / Imprimir Fatura (PDF)</span>
                                </a>
                            ) : (
                                <button
                                    className="btn-print-invoice"
                                    onClick={() => alert('Fatura emitida com sucesso. O documento está disponível.')}
                                >
                                    <FileText size={18} />
                                    <span>Fatura Registada</span>
                                </button>
                            )}
                            <button
                                className="btn-close-success"
                                onClick={() => setShowInvoiceSuccessModal(false)}
                            >
                                Concluir
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* ── Member Edit Wizard (shared with PeopleManager) ── */}
            <PersonWizardModal
                isOpen={editMemberUserId !== null}
                userId={editMemberUserId}
                onClose={() => setEditMemberUserId(null)}
                onSaved={fetchPaymentStatuses}
            />

            {/* ── History Modal ── */}
            {isHistoryModalOpen && (
                <PaymentHistorySocio
                    isOpen={isHistoryModalOpen}
                    onClose={() => {
                        setIsHistoryModalOpen(false);
                        setSelectedUserId(null);
                        fetchPaymentStatuses();
                    }}
                    userId={selectedUserId}
                    isAdmin={true}
                    onPaymentSuccess={fetchPaymentStatuses}
                    overdueMonths={athletes.find(a => a.userId === selectedUserId)?.overdueMonths || []}
                    quotaAmount={athletes.find(a => a.userId === selectedUserId)?.amount}
                    paymentStatus={athletes.find(a => a.userId === selectedUserId)?.status}
                    paymentPreference={athletes.find(a => a.userId === selectedUserId)?.paymentPreference || 'Monthly'}
                    selectedMonth={currentMonth}
                    selectedYear={currentYear}
                />
            )}

            {/* ── Daily Invoices Modal (Moloni) ── */}
            <DailyInvoicesModal
                isOpen={isDailyInvoicesModalOpen}
                onClose={() => setIsDailyInvoicesModalOpen(false)}
            />

            {/* ── Custom Quota Styles ── */}
            <style>{`
                .quota-display-container {
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    gap: 6px;
                }
                .quota-price-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                .custom-quota-badge {
                    background: #fef3c7;
                    color: #d97706;
                    font-size: 9px;
                    font-weight: 800;
                    padding: 1px 4px;
                    border-radius: 4px;
                    border: 1px solid #fde68a;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    line-height: 1.2;
                }
                .quota-actions {
                    display: flex;
                    align-items: center;
                    gap: 2px;
                }
                .quota-icon-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 3px;
                    border-radius: 4px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    color: #64748b;
                    transition: all 0.15s ease;
                }
                .quota-icon-btn.edit:hover {
                    color: #2563eb;
                    background: #eff6ff;
                }
                .quota-icon-btn.reset {
                    color: #d97706;
                }
                .quota-icon-btn.reset:hover {
                    color: #b45309;
                    background: #fef3c7;
                }

                /* Quota Edit Container */
                .quota-edit-container {
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    gap: 4px;
                }
                .quota-input { 
                    width: 72px !important; 
                    padding: 0.3rem 0.4rem !important; 
                    border: 2px solid #3b82f6 !important; 
                    border-radius: 6px !important; 
                    font-size: 0.85rem !important; 
                    font-weight: 700 !important; 
                    color: #1e293b !important; 
                    outline: none !important; 
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important; 
                    text-align: center !important; 
                    background: white !important;
                }
                .quota-edit-actions {
                    display: flex;
                    align-items: center;
                    gap: 2px;
                }
                .quota-action-btn {
                    width: 22px;
                    height: 22px;
                    border-radius: 4px;
                    border: none;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }
                .quota-action-btn.save {
                    background: #10b981;
                    color: white;
                }
                .quota-action-btn.save:hover {
                    background: #059669;
                }
                .quota-action-btn.reset {
                    background: #f59e0b;
                    color: white;
                }
                .quota-action-btn.reset:hover {
                    background: #d97706;
                }
                .quota-action-btn.cancel {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #cbd5e1;
                }
                .quota-action-btn.cancel:hover {
                    background: #e2e8f0;
                    color: #334155;
                }

                .monthly-fee { cursor: pointer; transition: color 0.2s; }
                .monthly-fee:hover { color: #3b82f6 !important; }
                .custom-price { color: #b45309 !important; font-weight: 700 !important; text-decoration: underline dotted #d97706; }

                /* Breakdown Popover */
                .breakdown-popover {
                    position: absolute;
                    top: calc(100% + 8px);
                    left: 50%;
                    transform: translateX(-50%);
                    z-index: 9999;
                    background: #1e293b;
                    color: #f1f5f9;
                    border-radius: 12px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.45);
                    min-width: 260px;
                    max-width: 320px;
                    padding: 0;
                    font-size: 13px;
                    white-space: nowrap;
                    overflow: hidden;
                }
                .breakdown-popover-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px 14px 8px;
                    border-bottom: 1px solid rgba(255,255,255,0.1);
                    font-weight: 700;
                    font-size: 12px;
                    letter-spacing: 0.04em;
                    text-transform: uppercase;
                    color: #94a3b8;
                }
                .breakdown-popover-header button {
                    background: none; border: none; color: #94a3b8; cursor: pointer; font-size: 14px; line-height: 1;
                }
                .breakdown-popover-header button:hover { color: #f1f5f9; }

                /* Breakdown Custom Notice */
                .breakdown-custom-notice {
                    background: rgba(245, 158, 11, 0.15);
                    border-bottom: 1px solid rgba(245, 158, 11, 0.25);
                    padding: 8px 12px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                }
                .breakdown-custom-text {
                    display: flex;
                    flex-direction: column;
                    gap: 1px;
                }
                .breakdown-custom-text span {
                    font-size: 11.5px;
                    font-weight: 700;
                    color: #fbbf24;
                }
                .breakdown-custom-text small {
                    font-size: 10px;
                    color: #cbd5e1;
                }
                .breakdown-reset-btn {
                    background: #d97706;
                    color: white;
                    border: none;
                    border-radius: 5px;
                    padding: 4px 8px;
                    font-size: 10.5px;
                    font-weight: 700;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    transition: background 0.15s ease;
                    white-space: nowrap;
                }
                .breakdown-reset-btn:hover {
                    background: #b45309;
                }

                .breakdown-list {
                    list-style: none; margin: 0; padding: 8px 0;
                }
                .breakdown-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 5px 14px;
                    gap: 12px;
                    white-space: normal;
                }
                .breakdown-item:hover { background: rgba(255,255,255,0.05); }
                .breakdown-label { flex: 1; color: #cbd5e1; font-size: 12.5px; }
                .breakdown-amount { font-weight: 700; color: #f1f5f9; font-variant-numeric: tabular-nums; white-space: nowrap; }
                .breakdown-discount .breakdown-label { color: #34d399; }
                .breakdown-discount .breakdown-amount { color: #34d399; }
                .breakdown-total {
                    display: flex; justify-content: space-between;
                    padding: 9px 14px;
                    border-top: 1px solid rgba(255,255,255,0.12);
                    font-size: 13.5px;
                    color: #f1f5f9;
                }
                .breakdown-discounts {
                    display: flex; flex-wrap: wrap; gap: 4px;
                    padding: 6px 14px 10px;
                }
                .breakdown-discount-tag {
                    background: rgba(52,211,153,0.15);
                    color: #34d399;
                    border-radius: 6px;
                    padding: 2px 7px;
                    font-size: 11px;
                    font-weight: 600;
                    border: 1px solid rgba(52,211,153,0.3);
                }
                .breakdown-warning {
                    padding: 6px 14px 10px;
                    color: #fbbf24;
                    font-size: 11.5px;
                }
                .breakdown-loading {
                    padding: 12px 14px;
                    color: #94a3b8;
                    text-align: center;
                }
            `}</style>
        </div>
    );
};

export default PaymentManager;